import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { LeviDrawerProvider, LeviLauncher, LeviAssistantDrawer, useLeviDrawer } from "@/components/LeviLauncher";
import { primeTts } from "@/lib/leviSpeech";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import UniversalSearch from "@/components/UniversalSearch";
import {
  LayoutDashboard,
  FileText,
  Send,
  FileSignature,
  Receipt,
  MessageSquare,
  Settings,
  CreditCard,
  Shield,
  LogOut,
  Languages,  // eslint-disable-line no-unused-vars
  FolderKanban,
  Menu,
  X,
  Sparkles,
  Sparkle,
  GitPullRequest,
  ClipboardList,
  Users,
  ShieldCheck,
  Building2,
  ClipboardCheck,
  HeartHandshake,
  CalendarDays,
  Home,
} from "lucide-react";

// V-P1 Visual Reconciliation · Approved desktop sidebar structure.
// The permanent sidebar exposes ONLY the approved primary work / attention /
// help groups. Every underlying route the app previously exposed as a
// top-level sidebar destination remains functional and reachable through
// (a) the Attention deep-links below, (b) the Project Connected View,
// (c) Company & Settings, and (d) direct URL/navigation from within
// customer/project surfaces. Simplifying the permanent sidebar does NOT
// delete functionality.
const NAV_GROUPS = [
  {
    label: "Daily / Primary work", labelKey: "nav.daily_primary_work",
    items: [
      { to: "/app", label: "Dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, end: true },
      { to: "/app/customers", label: "Customers", labelKey: "nav.customers", icon: Users },
      { to: "/app/projects", label: "Projects", labelKey: "nav.projects", icon: FolderKanban },
      { to: "/app/schedule", label: "Job Calendar", labelKey: "nav.job_calendar", icon: CalendarDays },
      { to: "/app/invoices", label: "Invoices", labelKey: "nav.invoices", icon: Receipt },
      { to: "/app/estimates/new/levi", label: "Custom Job with Levi", labelKey: "nav.custom_job_with_levi", icon: Sparkle },
    ],
  },
  {
    label: "Attention", labelKey: "nav.attention",
    items: [
      { to: "/app/estimates", label: "Estimates awaiting review", labelKey: "nav.estimates_awaiting", icon: FileText },
      { to: "/app/proposals", label: "Proposals awaiting response", labelKey: "nav.proposals_awaiting", icon: Send },
      { to: "/app/contracts", label: "Contracts awaiting signature", labelKey: "nav.contracts_awaiting", icon: FileSignature },
      { label: "Change orders", labelKey: "nav.change_orders", icon: GitPullRequest, comingNext: true },
      { to: "/app/work-orders", label: "Work orders", labelKey: "nav.work_orders", icon: ClipboardList },
      { to: "/app/closeout", label: "Closeout / Warranty", labelKey: "nav.closeout_warranty", icon: ShieldCheck },
      { to: "/app/customer-updates", label: "Customer updates", labelKey: "nav.customer_updates", icon: MessageSquare },
    ],
  },
  {
    label: "Help / Company", labelKey: "nav.help_company",
    items: [
      { to: "/app/meet-levi", label: "Meet Levi", labelKey: "nav.meet_levi", icon: HeartHandshake },
      { to: "/app/settings", label: "Company & Settings", labelKey: "nav.company_and_settings", icon: Building2 },
    ],
  },
];

const slugify = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// LEVI-FIRST-01 v2 · Shell-integrated global Levi access control.
// Renders inside the authenticated shell header on every route so
// contractors can reach the existing Levi drawer without hunting for
// the lower-right floating launcher. Calls into the same
// `useLeviDrawer()` hook the launcher uses — no second provider,
// no second drawer instance, no second conversation session.
//
// A supplemental keyboard shortcut (⌥ + L / Alt + L) is intentionally
// non-conflicting with common browser and OS shortcuts.
const ShellLeviButton = () => {
  const { openDrawer } = useLeviDrawer();
  const { lang } = useLang();
  const es = lang === "es";
  const label = es ? "Levi" : "Levi";
  const aria = es ? "Hablar con Levi" : "Talk to Levi";

  useEffect(() => {
    const onKey = (e) => {
      // Alt + L (Option + L on macOS). Skip while the user is typing.
      if (!e.altKey || e.metaKey || e.ctrlKey) return;
      if ((e.key || "").toLowerCase() !== "l") return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (t && t.isContentEditable)) return;
      e.preventDefault();
      primeTts("shell-levi-shortcut");
      openDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDrawer]);

  const handleClick = () => {
    primeTts("shell-levi-btn");
    openDrawer();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="shell-levi-access-btn"
      aria-label={aria}
      title={`${aria} (Alt+L)`}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs sm:text-sm font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm border border-red-700/20 transition"
    >
      <Sparkle size={14} className="text-white/90" />
      <span>{label}</span>
    </button>
  );
};

const SidebarBody = ({ user, logout, onLinkClick, wizardIncomplete }) => {
  const { t } = useLang();
  return (
  <>
    <div className="px-4 py-6 border-b border-white/10">
      {/* Sidebar logo · PO-authoritative asset (2026-08-22 correction).
          The prior implementation used `/brand/logo-full-dark.svg`,
          a repository-generated reconstruction. Corrected to use the
          authoritative approved asset the PO attached:
          `/brand/JobClose_AI_Approved_Dark_Background_Logo.webp`.
          Composition preserved verbatim (icon + JobClose AI horizontal
          + tagline baked in). A native HTML rendering of the tagline
          is layered directly below to guarantee readability at normal
          desktop viewing size, per PO instruction — the approved
          artwork itself is not modified. */}
      <Link
        to="/app"
        aria-label="JobClose AI"
        data-testid="sidebar-logo"
        className="flex flex-col items-start w-full gap-1"
      >
        <img
          src="/brand/JobClose_AI_Approved_Dark_Background_Logo.webp"
          alt="JobClose AI"
          className="block w-full max-w-[220px] h-auto select-none"
          draggable={false}
          data-testid="sidebar-logo-img"
        />
        <div
          className="text-[11px] font-medium text-white/80 leading-tight tracking-tight pl-1"
          data-testid="sidebar-logo-tagline"
          aria-hidden="true"
        >
          Built by a contractor, for contractors.
        </div>
      </Link>
    </div>
    {wizardIncomplete && (
      <div className="px-4 pt-3" data-testid="resume-setup-pill-wrap">
        <NavLink
          to="/app/setup"
          onClick={onLinkClick}
          data-testid="resume-setup-pill"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold bg-[var(--jc-blue)]/20 text-white border border-[var(--jc-blue)]/50 hover:bg-[var(--jc-blue)]/30"
        >
          <Sparkles size={14} /> {t("nav.continue_setup")}
        </NavLink>
      </div>
    )}
    <nav className="flex-1 px-3 py-4 overflow-y-auto">
      {NAV_GROUPS.map((group, gi) => (
        <div key={group.label} className={gi > 0 ? "mt-5 pt-4 border-t border-white/10" : ""}>
          <div
            data-testid={`nav-group-${slugify(group.label)}`}
            className="px-3 mb-2 text-[10px] font-bold tracking-widest uppercase text-white/40"
          >
            {group.labelKey ? t(group.labelKey) : group.label}
          </div>
          {group.items.map((n) => {
            const testid = `nav-${slugify(n.label)}`;
            const displayLabel = n.labelKey ? t(n.labelKey) : n.label;
            if (n.comingNext) {
              return (
                <div
                  key={n.label}
                  data-testid={testid}
                  aria-disabled="true"
                  title="Coming next"
                  className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm mb-1 text-white/35 cursor-not-allowed select-none"
                >
                  <n.icon size={16} />
                  <span className="truncate">{displayLabel}</span>
                  <span
                    data-testid={`${testid}-dot`}
                    aria-hidden="true"
                    className="ml-auto w-1.5 h-1.5 rounded-full bg-white/25 shrink-0"
                  />
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={onLinkClick}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm mb-1 transition ${
                    isActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <n.icon size={16} />
                <span>{displayLabel}</span>
              </NavLink>
            );
          })}
        </div>
      ))}
      {user?.role === "admin" && (
        <NavLink
          to="/app/admin"
          onClick={onLinkClick}
          data-testid="nav-admin"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm mb-1 mt-4 border-t border-white/10 pt-4 transition ${
              isActive ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
            }`
          }
        >
          <Shield size={16} /> <span>Admin</span>
        </NavLink>
      )}
    </nav>
    <div className="p-4 border-t border-white/10 text-xs text-white/60">
      <div className="mb-1 font-medium text-white/80">{user?.full_name}</div>
      <div className="truncate">{user?.email}</div>
      <button
        onClick={logout}
        data-testid="logout-button"
        className="mt-3 flex items-center gap-2 text-white/70 hover:text-white"
      >
        <LogOut size={14} /> {t("nav.sign_out")}
      </button>
    </div>
  </>
  );
};

// V-P1 · Mobile bottom navigation.
// Approved five-item permanent bottom nav for mobile only:
//   Home · Customers · Levi (center) · Projects · More
// Levi remains the visually prominent center coworker anchor. Custom
// Job with Levi lives inside "More" per PO direction and never
// replaces one of the five permanent destinations.
//
// The nav is rendered inside the Levi drawer provider so the center
// button reuses the existing shared Levi drawer instance — no second
// drawer, no second conversation session. Hidden at lg and above so
// the desktop sidebar remains the primary desktop navigation.
const MobileBottomNav = () => {
  const { openDrawer } = useLeviDrawer();
  const location = useLocation();
  const { t } = useLang();
  const path = location.pathname;
  const isHome = path === "/app" || path === "/app/";
  const isCustomers = path.startsWith("/app/customers") || path.startsWith("/app/jobs");
  const isProjects = path.startsWith("/app/projects");
  const isMore =
    !isHome &&
    !isCustomers &&
    !isProjects &&
    path.startsWith("/app");

  const handleLevi = () => {
    primeTts("mobile-bottom-nav-levi");
    openDrawer();
  };

  const base =
    "flex flex-col items-center justify-center gap-0.5 py-1.5 text-[10px] font-semibold transition";
  const inactive = "text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]";
  const active = "text-[var(--jc-blue)]";

  return (
    <nav
      data-testid="mobile-bottom-nav"
      aria-label={t("nav.mobile_bottom_nav")}
      className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-white border-t border-[var(--jc-border)] pb-[env(safe-area-inset-bottom,0px)]"
    >
      <div className="grid grid-cols-5 items-end">
        <Link
          to="/app"
          data-testid="mbn-home"
          className={`${base} ${isHome ? active : inactive}`}
        >
          <Home size={20} strokeWidth={2} />
          <span>{t("nav.home")}</span>
        </Link>
        <Link
          to="/app/customers"
          data-testid="mbn-customers"
          className={`${base} ${isCustomers ? active : inactive}`}
        >
          <Users size={20} strokeWidth={2} />
          <span>{t("nav.customers_short")}</span>
        </Link>
        <button
          type="button"
          onClick={handleLevi}
          data-testid="mbn-levi"
          aria-label={t("nav.levi_open")}
          className="flex flex-col items-center justify-end pb-1 pt-0"
        >
          <span className="-mt-4 mb-0.5 w-14 h-14 rounded-full bg-[var(--jc-blue)] text-white shadow-lg border-4 border-white flex items-center justify-center">
            <Sparkle size={22} className="text-white" fill="currentColor" />
          </span>
          <span className="text-[10px] font-bold text-[var(--jc-blue)]">
            {t("nav.levi_short")}
          </span>
        </button>
        <Link
          to="/app/projects"
          data-testid="mbn-projects"
          className={`${base} ${isProjects ? active : inactive}`}
        >
          <FolderKanban size={20} strokeWidth={2} />
          <span>{t("nav.projects_short")}</span>
        </Link>
        <button
          type="button"
          onClick={() => {
            const btn = document.querySelector('[data-testid="mobile-nav-open"]');
            if (btn) btn.click();
          }}
          data-testid="mbn-more"
          className={`${base} ${isMore ? active : inactive}`}
        >
          <Menu size={20} strokeWidth={2} />
          <span>{t("nav.more_short")}</span>
        </button>
      </div>
    </nav>
  );
};

const DashboardLayout = ({ children, title, subtitle, actions }) => {
  const { user, logout } = useAuth();
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Lightweight poll of the pricing profile to decide whether to show the
  // "Resume pricing setup" pill. Failures are non-fatal — the pill simply
  // stays hidden. We deliberately do NOT redirect anyone here; legacy and
  // demo accounts already report setup_completed=true.
  const [wizardIncomplete, setWizardIncomplete] = useState(false);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api
      .get("/pricing-profile")
      .then((r) => {
        if (!cancelled) setWizardIncomplete(r.data?.setup_completed === false);
      })
      .catch(() => {
        if (!cancelled) setWizardIncomplete(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="min-h-screen flex bg-[var(--jc-bg)]">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-[var(--jc-navy)] text-white sticky top-0 h-screen">
        <SidebarBody user={user} logout={logout} wizardIncomplete={wizardIncomplete} />
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileNavOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-nav-drawer">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <aside className="relative flex flex-col w-72 max-w-[85vw] bg-[var(--jc-navy)] text-white h-full">
            <button
              onClick={() => setMobileNavOpen(false)}
              data-testid="mobile-nav-close"
              className="absolute top-3 right-3 p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
            <SidebarBody user={user} logout={logout} onLinkClick={() => setMobileNavOpen(false)} wizardIncomplete={wizardIncomplete} />
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/80 border-b border-[var(--jc-border)]">
          <div className="px-4 lg:px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 shrink">
              <button
                onClick={() => setMobileNavOpen(true)}
                data-testid="mobile-nav-open"
                className="lg:hidden p-1.5 rounded-md border border-[var(--jc-border)] text-[var(--jc-text)] hover:bg-[var(--jc-bg)]"
                aria-label="Open menu"
              >
                <Menu size={18} />
              </button>
              <div className="min-w-0">
                {title && <h1 className="font-display text-lg sm:text-xl tracking-tight truncate">{title}</h1>}
              </div>
            </div>
            {/* V-P1 Visual Reconciliation · Universal Search gets its
                own dedicated header slot on desktop. Sized generously
                (min 380px, max 640px) so the search field is clearly
                prominent even when the title area has long copy. The
                page subtitle was moved out of the header row into the
                page body below so it can no longer squeeze the search
                input into a magnifying-glass. */}
            <div className="hidden lg:flex flex-1 justify-center min-w-[380px] max-w-2xl px-4">
              <UniversalSearch />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ShellLeviButton />
              {/* Slice 2D1 · segmented English | Español toggle.
                  Two explicit segments — one click on either segment
                  lands on that exact locale (never derives next value
                  from stale state). Active segment is visually
                  unmistakable via filled background + strong text; the
                  inactive segment stays outlined and dimmed. */}
              <div
                data-testid="lang-toggle"
                role="group"
                aria-label="Language"
                className="inline-flex items-center rounded-md border border-[var(--jc-border)] p-0.5 bg-white"
              >
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  aria-pressed={lang === "en"}
                  data-testid="lang-toggle-en"
                  data-active={lang === "en" ? "true" : "false"}
                  className={
                    "px-2.5 py-1 text-xs font-semibold rounded-[5px] transition " +
                    (lang === "en"
                      ? "bg-[var(--jc-navy)] text-white"
                      : "text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]")
                  }
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => setLang("es")}
                  aria-pressed={lang === "es"}
                  data-testid="lang-toggle-es"
                  data-active={lang === "es" ? "true" : "false"}
                  className={
                    "px-2.5 py-1 text-xs font-semibold rounded-[5px] transition " +
                    (lang === "es"
                      ? "bg-[var(--jc-navy)] text-white"
                      : "text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]")
                  }
                >
                  Español
                </button>
              </div>
              {actions}
            </div>
          </div>
          {/* V-P1 · Mobile Universal Search bar. Renders under the
              header on phones/tablets where the header row itself
              cannot host the full-width input. Uses the same
              component so behavior is identical. */}
          <div className="lg:hidden px-4 pb-3">
            <UniversalSearch compact />
          </div>
        </header>
        {user?.is_demo && (
          <div
            data-testid="demo-account-banner"
            className="bg-[var(--jc-yellow)]/15 border-b border-yellow-300 px-4 lg:px-6 py-2 text-xs text-yellow-900 flex items-center gap-2 justify-center"
          >
            <span className="inline-block px-1.5 py-0.5 rounded bg-yellow-300 text-yellow-900 font-bold text-[10px] tracking-wider">
              DEMO ACCOUNT
            </span>
            <span>
              {user.demo_disclaimer || "Demo account only. Sample pricing and sample customer data. Contractors must enter and approve their own pricing."}
            </span>
          </div>
        )}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
          {subtitle && (
            <p className="text-sm text-[var(--jc-text-soft)] -mt-2 mb-4">
              {subtitle}
            </p>
          )}
          {children}
        </main>
      </div>
      {/* V-P1 · Mobile permanent bottom nav (Home · Customers · Levi · Projects · More).
          Rendered inside the LeviDrawerProvider wrapper below so the center Levi
          button reuses the same drawer instance the shell header uses. */}
      <MobileBottomNav />
      {/* V-P1 Visual Reconciliation · floating LeviLauncher visual button
          UN-MOUNTED per PO direction. LeviAssistantDrawer + useLeviDrawer
          hook + LeviDrawerProvider are all preserved so every approved
          entry point (mobile bottom-nav center button, shell header Levi
          action, contextual Ask-Levi buttons) continues to open the
          same drawer instance and same conversation session. */}
      <LeviAssistantDrawer />
    </div>
  );
};

// Wrap the exported layout in the drawer provider so any descendant
// (the launcher, the Meet Levi character button, and future contractor
// surfaces) shares a single drawer instance.
const DashboardLayoutWithLevi = (props) => (
  <LeviDrawerProvider>
    <DashboardLayout {...props} />
  </LeviDrawerProvider>
);

export default DashboardLayoutWithLevi;
